import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Fab, IconButton, Snackbar, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Colors from '../asstes/color';
import MessageIcon from '../asstes/images/msg.png';
import { Context as ContactContext } from '../context/ContactContext';

const emptyForm = { name: '', email: '', subject: '', message: '' };

// The original floating contact form, kept separate from the responsive hero.
export default function ContactUs() {
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(() => window.scrollY > 80);
  useEffect(() => {
    const update = () => setCompact(window.scrollY > 80);
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, []);
  const [form, setForm] = useState(emptyForm);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const [sent, setSent] = useState(false);
  const toggle = useRef(null);
  const { createMessage } = useContext(ContactContext);
  function close() {
    if (pending) return;
    setOpen(false);
    toggle.current?.focus();
  }
  async function submit(event) {
    event.preventDefault();
    setPending(true);
    setError(false);
    try {
      const success = await createMessage(form);
      if (success) {
        setForm(emptyForm);
        setOpen(false);
        setSent(true);
        toggle.current?.focus();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  }
  return <>
    {open && <Box component="section" id="contact-form" role="dialog" aria-labelledby="contact-title"
      onKeyDown={event => { if (event.key === 'Escape') close(); }}
      sx={{ position: 'fixed', bottom: 92, right: 20, width: { xs: 200, md: 300 }, maxWidth: 'calc(100vw - 72px)', maxHeight: 'calc(100dvh - 140px)', overflowY: 'auto', boxSizing: 'content-box', background: 'white', zIndex: 1200, borderRadius: '28px', p: 2, boxShadow: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography id="contact-title" sx={{ fontWeight: 600, fontFamily: 'MyCustomFont1', fontSize: { xs: 20, md: 24 }, color: Colors.textSecondary }}>Contact Us!</Typography>
        <IconButton aria-label="Close contact form" disabled={pending} onClick={close} sx={{ color: Colors.textSecondary }}><CloseIcon sx={{ fontSize: 30 }}/></IconButton>
      </Box>
      <Box component="form" onSubmit={submit}>
        {error && <Alert severity="error">Your message could not be sent. Please try again. Your details have been kept.</Alert>}
        <Box sx={{ width: '90%' }}>
          {Object.keys(emptyForm).map(name => <React.Fragment key={name}>
            <Typography component="label" htmlFor={`contact-${name}`} variant="subtitle1" sx={{ display: 'block', color: Colors.textSecondary, fontFamily: 'MyCustomFont1', fontWeight: 600 }}>
              {name[0].toUpperCase() + name.slice(1)}
            </Typography>
            <TextField id={`contact-${name}`} name={name} type={name === 'email' ? 'email' : 'text'} fullWidth required margin="dense" variant="outlined"
              value={form[name]} disabled={pending} multiline={name === 'message'} rows={name === 'message' ? 3 : undefined}
              onChange={event => setForm({ ...form, [name]: event.target.value })}
              inputProps={{ maxLength: name === 'message' ? 5000 : 254 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '30px', backgroundColor: Colors.white, color: Colors.textSecondary,
                  ...(name !== 'message' && { height: 30 }),
                  '& input': { padding: '4px 12px', color: Colors.textSecondary },
                  '& fieldset': { borderColor: Colors.textSecondary, borderWidth: 3 },
                  '&:hover fieldset, &.Mui-focused fieldset': { borderColor: Colors.textSecondary },
                },
              }}/>
          </React.Fragment>)}
        </Box>
        <Box mt={2} display="flex" justifyContent="center">
          <Button type="submit" variant="contained" disabled={pending} sx={{ background: Colors.textSecondary, borderRadius: '25px' }}>{pending ? 'Sending…' : 'Send'}</Button>
        </Box>
      </Box>
    </Box>}
    <Fab ref={toggle} aria-label="Contact Us" data-compact={compact} variant="extended" size="medium" color="primary" aria-expanded={open} aria-controls={open ? 'contact-form' : undefined}
      sx={{ position: 'fixed', bottom: 36, right: { xs: 20, md: 40 }, zIndex: 1100, height: 48, width: compact ? 48 : 168, minWidth: 48, px: compact ? 0 : 2.5, fontSize: 16, transition: 'width 180ms ease, padding 180ms ease', '@media(prefers-reduced-motion: reduce)': { transition: 'none' }, backgroundColor: '#2BB1E6', textTransform: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={() => { if (pending) return; setOpen(value => !value); setError(false); }}>
      <Box component="img" src={MessageIcon} alt="" sx={{ width: 24, height: 24, flexShrink: 0, marginRight: compact ? 0 : 1, transition: 'margin 180ms ease', '@media(prefers-reduced-motion: reduce)': { transition: 'none' } }}/>
      <Box component="span" sx={{ width: compact ? 0 : 'auto', overflow: 'hidden', whiteSpace: 'nowrap', opacity: compact ? 0 : 1 }}>Contact Us</Box>
    </Fab>
    <Snackbar open={sent} autoHideDuration={3000} onClose={() => setSent(false)}>
      <Alert onClose={() => setSent(false)} severity="success" variant="filled">Message Sent!</Alert>
    </Snackbar>
  </>;
}
